import { FC } from 'react'
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { DataLoad } from 'src/components/ui'
import { useSensorQualityControlRequest } from 'src/hooks/api'
import { formatChecksPassed } from './qualityControlPresentation'
import { SensorQualityControlTable } from './sensorQualityControlTable'

interface SensorQualityControlProps {
  sensorUniqueId: string
}

/**
 * Non-collapsible Quality Control section of the sensor details page. Runs the checks as soon as
 * the page is opened and shows the "N of 5 checks passed" rating next to the section title.
 */
export const SensorQualityControl: FC<SensorQualityControlProps> = ({ sensorUniqueId }) => {
  const { isInitialLoading, isError, error, data, refetch, isFetching } = useSensorQualityControlRequest({
    uniqueIds: [sensorUniqueId]
  })

  const row = data?.rows?.[0]

  return (
    <Grid container flexDirection="column" wrap="nowrap">
      <Grid container item alignItems="center" spacing={2} mb={1}>
        <Grid item>
          <Typography variant="h6">Quality Control</Typography>
        </Grid>
        {row ? (
          <Grid item>
            <Typography variant="body1" color={row.passed ? 'text.secondary' : 'error'}>
              {formatChecksPassed(row.passedChecks, row.totalChecks)}
            </Typography>
          </Grid>
        ) : null}
        <Grid item>
          <Button variant="text" size="small" startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />} disabled={isFetching} onClick={() => refetch()}>
            Re-check
          </Button>
        </Grid>
      </Grid>
      <Grid item sx={{ width: '100%', minWidth: 0 }}>
        <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
          {row ? (
            <SensorQualityControlTable rows={[row]} autoHeight />
          ) : (
            <Box py={2}>
              <Typography color="text.secondary">Quality control data is not available for this sensor.</Typography>
            </Box>
          )}
        </DataLoad>
      </Grid>
    </Grid>
  )
}
